import { FC, ReactNode, useState } from 'react';
import axios, {
  Axios,
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { i18n, LANGUAGE_LABELS } from '@atlas-frontend/i18n';
import { isObject } from './utils';

export const API_BASE_URL = '/backend/api';

type Event = 'request' | 'response';
type Listener<E extends Event> = E extends 'request'
  ? (
      value: AxiosRequestConfig,
    ) => AxiosRequestConfig | Promise<AxiosRequestConfig>
  : (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
type ErrorListener = (error: AxiosError) => Promise<never>;

type JSONPath = string;

export interface ApiErrorMeta {
  args?: string[] | Record<string | number, string>;
  level?: 'warning' | 'error';
}

export interface ApiError {
  code: string;
  path?: JSONPath;
  source?: {
    pointer?: JSONPath;
  };
  meta?: ApiErrorMeta;
}

export interface ApiErrorResponseBody {
  errors: ApiError[];
}

export type ApiResponse<T = never> = AxiosResponse<T>;
export type ApiErrorResponse<D = any> = AxiosError<ApiErrorResponseBody, D>;

interface ApiEventSubscriptionOptions<E extends Event> {
  event: E;
  onSuccess?: Listener<E>;
  onError?: ErrorListener;
}

interface ApiClient extends Axios {
  subscribe<E extends Event>(
    options: ApiEventSubscriptionOptions<E>,
  ): () => void;
}

function subscribe<E extends Event>(
  this: Axios,
  options: ApiEventSubscriptionOptions<E>,
) {
  const { event, onSuccess, onError } = options;
  const interceptor = this.interceptors[event].use(onSuccess as any, onError);
  return () => this.interceptors.request.eject(interceptor);
}

const errorMsg =
  'ApiClient is not instantiated. Did you forget to implement ApiClientProvider?';

const unimplementedApiClient: ApiClient = {
  patchForm(): any {
    throw new Error(errorMsg);
  },
  postForm(): any {
    throw new Error(errorMsg);
  },
  putForm(): any {
    throw new Error(errorMsg);
  },
  get defaults(): any {
    throw new Error(errorMsg);
  },
  get interceptors(): any {
    throw new Error(errorMsg);
  },
  getUri() {
    throw new Error(errorMsg);
  },
  query() {
    throw new Error(errorMsg);
  },
  head() {
    throw new Error(errorMsg);
  },
  options() {
    throw new Error(errorMsg);
  },
  request() {
    throw new Error(errorMsg);
  },
  get() {
    throw new Error(errorMsg);
  },
  post() {
    throw new Error(errorMsg);
  },
  put() {
    throw new Error(errorMsg);
  },
  patch() {
    throw new Error(errorMsg);
  },
  delete() {
    throw new Error(errorMsg);
  },
  subscribe() {
    throw new Error(errorMsg);
  },
};

export let apiClient: ApiClient = unimplementedApiClient;

export const initApiClient = () => {
  apiClient = Object.assign(axios.create({ baseURL: API_BASE_URL }), {
    subscribe,
  });
  // The backend renders some texts itself (Excel reports); tell it the UI language.
  // The labels pseudo-language has no backend counterpart, so it is not sent.
  apiClient.interceptors.request.use((config) => {
    const language = i18n.language;
    if (language && language !== LANGUAGE_LABELS) {
      config.headers = AxiosHeaders.from(config.headers);
      config.headers.set('Accept-Language', language);
    }
    return config;
  });
  return apiClient;
};

export const ApiClientProvider: FC<{ children: ReactNode }> = (props) => {
  useState(initApiClient);
  return props.children;
};

export const isApiErrorResponse = (value?: any): value is AxiosError =>
  value?.isAxiosError;

export const isApiServerError = (error?: any): boolean =>
  isApiErrorResponse(error) && isWithStatus(error, 500, 599);

export const isApiSessionExpiredOrNotStarted = (error?: any): boolean =>
  isApiErrorResponse(error) && isWithStatus(error, 401);

export const isForbidden = (error?: any): boolean =>
  isApiErrorResponse(error) && isWithStatus(error, 403);

export const isNotFound = (error?: any): boolean =>
  isApiErrorResponse(error) && isWithStatus(error, 404);

export const isWithStatus = (error: AxiosError, ...statuses: number[]) => {
  if (!error.response) {
    return false;
  }
  const { status } = error.response;
  const lowest = Math.min(...statuses);
  const highest = Math.max(...statuses);
  return lowest <= status && status <= highest;
};

export const isApiError = (value?: any): value is ApiError =>
  isObject(value) &&
  'code' in value &&
  typeof value.code === 'string' &&
  value.code.length > 0;

export const isApiErrorResponseBody = (
  value?: any,
): value is ApiErrorResponseBody =>
  isObject(value) &&
  'errors' in value &&
  Array.isArray(value.errors) &&
  value.errors.length > 0 &&
  value.errors.every(isApiError);

export const isApiErrorWithoutErrorsBody = (error?: any) =>
  isApiErrorResponse(error) && !isApiErrorResponseBody(error.response?.data);

export type ForbiddenVariant = 'forbidden' | 'missing_view_permission';

export const getForbiddenErrorCode = (
  error?: any,
): ForbiddenVariant | undefined =>
  (isForbidden(error) && getErrorCode(error)) as ForbiddenVariant | undefined;

export const getErrorCode = (error?: any): string | undefined =>
  isApiErrorResponse(error) && isApiErrorResponseBody(error.response?.data)
    ? error.response.data.errors[0].code
    : undefined;
